import { CJMDto, CJMFiltersDto } from '@/api/Metrics/Types/CJMDto'
import { TransactionProcessingMetricsDto } from '@/api/Metrics/Types/TransactionProcessingMetricsDto'

import {
  MetricsCountDto,
  MetricsDepositsDto,
  MetricsIBCommissionsDto,
  MetricsLotsDto,
  MetricsNetDepositsDto,
  MetricsNVDto,
  MetricsQueryParams,
} from '../../api/Metrics/Types/MetricsDto'
import { ApiClient } from './ApiClient'

export class MetricsApiClient {
  constructor(private apiClient: ApiClient) {}

  getNVMetrics: (queryParams: MetricsQueryParams) => Promise<MetricsNVDto[]> = (queryParams) => {
    return this.apiClient.get(
      `partnerships/introducing-brokers/metrics/nv-traded?${getSearchParams(queryParams)}`
    )
  }
  getLotsMetrics: (queryParams: MetricsQueryParams) => Promise<MetricsLotsDto[]> = (
    queryParams
  ) => {
    return this.apiClient.get(
      `partnerships/introducing-brokers/metrics/lots?${getSearchParams(queryParams)}`
    )
  }
  getFTDMetrics: (queryParams: MetricsQueryParams) => Promise<MetricsCountDto[]> = (
    queryParams
  ) => {
    return this.apiClient.get(`wallets/metrics/first-time-deposit?${getSearchParams(queryParams)}`)
  }
  getDepositsMetrics: (queryParams: MetricsQueryParams) => Promise<MetricsDepositsDto[]> = (
    queryParams
  ) => {
    return this.apiClient.get(`wallets/metrics/deposits?${getSearchParams(queryParams)}`)
  }
  getNetDepositsMetrics: (queryParams: MetricsQueryParams) => Promise<MetricsNetDepositsDto[]> = (
    queryParams
  ) => {
    return this.apiClient.get(`wallets/metrics/net-deposits?${getSearchParams(queryParams)}`)
  }
  getNewClientsMetrics: (queryParams: MetricsQueryParams) => Promise<MetricsCountDto[]> = (
    queryParams
  ) => {
    return this.apiClient.get(
      `partnerships/introducing-brokers/metrics/new-clients?${getSearchParams(queryParams)}`
    )
  }
  getActiveClientsMetrics: (
    queryParams: Omit<MetricsQueryParams, 'from' | 'to'>
  ) => Promise<MetricsCountDto> = (queryParams) => {
    return this.apiClient.get(
      `trading-accounts/metrics/active-clients?${getSearchParams(queryParams)}`
    )
  }
  getOpenedTAMetrics: (queryParams: MetricsQueryParams) => Promise<MetricsCountDto[]> = (
    queryParams
  ) => {
    return this.apiClient.get(
      `partnerships/introducing-brokers/metrics/trading-accounts-opened?${getSearchParams(queryParams)}`
    )
  }
  getActiveTAMetrics: (
    queryParams: Omit<MetricsQueryParams, 'from' | 'to'>
  ) => Promise<MetricsCountDto> = (queryParams) => {
    return this.apiClient.get(
      `partnerships/introducing-brokers/metrics/active-trading-accounts?${getSearchParams(queryParams)}`
    )
  }
  getIBCommissionsMetrics: (queryParams: MetricsQueryParams) => Promise<MetricsIBCommissionsDto[]> =
    (queryParams) => {
      return this.apiClient.get(
        `partnerships/introducing-brokers/metrics/commissions?${getSearchParams(queryParams)}`
      )
    }
  getCJMMetrics: (queryParams: CJMFiltersDto) => Promise<CJMDto> = (queryParams) => {
    const baseUrl = 'partnerships/introducing-brokers/metrics/client-journey-map/totals'
    const params = getCJMQueryParams(queryParams).toString()

    return this.apiClient.get(params ? `${baseUrl}?${params}` : baseUrl)
  }
  getTransactionProcessingMetrics: () => Promise<TransactionProcessingMetricsDto> = () => {
    return this.apiClient.get('wallets/metrics/transaction-durations')
  }
}

const getSearchParams = (queryParams: Partial<MetricsQueryParams>) => {
  const searchParams = new URLSearchParams()

  if (queryParams.from) {
    searchParams.append('from', queryParams.from)
  }

  if (queryParams.to) {
    searchParams.append('to', queryParams.to)
  }

  if (queryParams.intervalType !== undefined) {
    searchParams.append('intervalType', queryParams.intervalType.toString())
  }

  queryParams.introducingBrokerIds?.forEach((id) => {
    searchParams.append('introducingBrokerIds', id)
  })

  queryParams.countryIds?.forEach((id) => {
    searchParams.append('countryIds', id)
  })

  return searchParams
}

const getCJMQueryParams = (queryParams: CJMFiltersDto) => {
  const searchParams = new URLSearchParams()

  queryParams.clientCountryIds?.forEach((id) => {
    searchParams.append('clientCountryIds', id)
  })

  queryParams.ibCountryIds?.forEach((id) => {
    searchParams.append('ibCountryIds', id)
  })

  if (queryParams.hasMainIb) {
    searchParams.append('hasMainIb', String(queryParams.hasMainIb))
  }

  return searchParams
}
